import React, { createContext, useState, useEffect, useContext } from 'react';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { red } from '@mui/material/colors';
import { getHomePageData } from '../utils/sanityQueries';

const Context = createContext();

export const GameContext = ({ children }) => {
    const [homePageData, setHomePageData] = useState({ banner: null, cores: {}, lastUpdated: null });
    const [loadingHomePage, setLoadingHomePage] = useState(false);
    const [errorHomePage, setErrorHomePage] = useState(null);

    const theme = createTheme({
        palette: {
            mode: 'dark',
            primary: {
                main: red[600],
            },
        },
        typography: {
            fontFamily: 'Poppins, Roboto, sans-serif'
        }
    });

    const loadCachedHomePageData = () => {
        try {
            const cachedData = localStorage.getItem('homePageData');
            if (cachedData) {
                return JSON.parse(cachedData);
            }
        } catch (error) {
            console.error("Erro ao carregar dados da página inicial do localStorage:", error);
        }
        return { banner: null, cores: {}, lastUpdated: null };
    };

    const saveHomePageDataToCache = (data) => {
        try {
            localStorage.setItem('homePageData', JSON.stringify(data));
        } catch (error) {
            console.error("Erro ao salvar dados da página inicial no localStorage:", error);
        }
    };

    const fetchHomePageData = async () => {
        setLoadingHomePage(true);
        setErrorHomePage(null);
        const data = await getHomePageData();
        if (data) {
            setHomePageData(data);
            saveHomePageDataToCache(data);
        } else {
            setErrorHomePage('Erro ao buscar dados da página inicial.');
        }
        setLoadingHomePage(false);
    };

    useEffect(() => {
        const cachedData = loadCachedHomePageData();
        setHomePageData(cachedData);

        const checkAndUpdateData = async () => {
            const freshData = await getHomePageData();
            if (freshData) {
                if (freshData.lastUpdated !== cachedData?.lastUpdated) {
                    setHomePageData(freshData);
                    saveHomePageDataToCache(freshData);
                } else if (!cachedData?.lastUpdated) {
                    // Se não havia cache, salva os dados iniciais
                    setHomePageData(freshData);
                    saveHomePageDataToCache(freshData);
                }
            }
        };

        checkAndUpdateData();

        // Opcional: Verificar atualizações periodicamente (ex: a cada hora)
        // const intervalId = setInterval(checkAndUpdateData, 3600000); // 1 hora
        // return () => clearInterval(intervalId); // Limpa o intervalo no desmontar
    }, []);

    const refetchHomePageData = async () => {
        await fetchHomePageData();
    };

    return (
        <Context.Provider
            value={{
                theme,
                homePageData,
                loadingHomePage,
                errorHomePage,
                refetchHomePageData,
            }}
        >
            <ThemeProvider
                theme={theme}
                disableTransitionOnChange
            >
                <CssBaseline />
                {children}
            </ThemeProvider>
        </Context.Provider>
    );
};

export const useGameContext = () => useContext(Context);